import {
  type DashboardLayoutItem,
  type DashboardLayoutState,
  type WidgetId,
  type WidgetInstance,
  DASHBOARD_LAYOUT_VERSION,
} from '../types';
import { WIDGET_REGISTRY } from '../widgetRegistry';

/**
 * Runtime-derived persona presets (decoupled from backend roles). They seed a
 * designed starting layout a user can then customize.
 */
export type PresetId = 'general' | 'fleet-manager' | 'work-order-collaborator';

export const PRESET_LABELS: Record<PresetId, string> = {
  general: 'General',
  'fleet-manager': 'Fleet Manager',
  'work-order-collaborator': 'Work Order Collaborator',
};

interface PresetPlacement {
  widgetId: WidgetId;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The launch dashboard: the four real-data + sample widgets that ship visible by
 * default (a KPI row of Critical Damages, Portfolio Capacity, and Repair Task
 * Status, with the Damage Detection Trend full-width below). The legacy Home
 * widgets (Attention Required, Upcoming Tasks, Fleet Map) stay in the catalog,
 * hidden by default, so users add them on demand.
 */
const LAUNCH_PLACEMENTS: PresetPlacement[] = [
  { widgetId: 'critical-damages', x: 0, y: 0, w: 4, h: 3 },
  { widgetId: 'portfolio-capacity', x: 4, y: 0, w: 4, h: 3 },
  { widgetId: 'repair-task-status', x: 8, y: 0, w: 4, h: 3 },
  { widgetId: 'damage-detection-trend', x: 0, y: 3, w: 12, h: 6 },
];

/**
 * Personas currently share the launch layout — every user starts from the same
 * designed default and customizes from there. Per-persona starting layouts can be
 * layered back in here later without touching the rest of the dashboard.
 */
const PRESET_LAYOUTS: Record<PresetId, PresetPlacement[]> = {
  general: LAUNCH_PLACEMENTS,
  'fleet-manager': LAUNCH_PLACEMENTS,
  'work-order-collaborator': LAUNCH_PLACEMENTS,
};

function buildLayout(placements: PresetPlacement[]): DashboardLayoutState {
  // Deterministic instanceId === widgetId (each widget appears once in a preset).
  const instances: WidgetInstance[] = placements.map(p => ({
    instanceId: p.widgetId,
    widgetId: p.widgetId,
  }));

  const lg: DashboardLayoutItem[] = placements.map(p => {
    const { minW, minH } = WIDGET_REGISTRY[p.widgetId].defaultLayout;
    return { i: p.widgetId, x: p.x, y: p.y, w: p.w, h: p.h, minW, minH };
  });

  return { version: DASHBOARD_LAYOUT_VERSION, instances, layouts: { lg } };
}

export function getPresetLayout(presetId: PresetId): DashboardLayoutState {
  return buildLayout(PRESET_LAYOUTS[presetId] ?? PRESET_LAYOUTS.general);
}
